import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import * as Dati from "src/Dati";
import * as Glob from "src/Globale";
import { Trad } from "src/components";
import ChiamaWeb, {CChiamaWebParametri} from "src/ChiamaWeb";
import useReducerOnChange from "src/hooks/ReducerOnChange";
import { IRouteParam } from "../../pages/Bond";
import { _CheckFileListRow } from "../../components";
import { AlertDialogContext } from "../../providers/AlertDialogProvider";

interface ICheckFileListProps {
    ID_TipoFileRichiesto: string;
    ValueJSON: Glob.DatiJSON;
    OnChange: (e: React.FormEvent<any> | string, val?: unknown) => void;
}

const useStyles = makeStyles(({ shape, spacing }: Theme) => ({
    icon: {
        marginRight: spacing(2)
    },
    table: {
        marginBottom: spacing(4)
    },
    title: {
        flexGrow: 1,
    },
    toolbar: {
        borderRadius: shape.borderRadius,
        marginTop: spacing(4),
        marginBottom: spacing(2)
    }
}));

export const _CheckFileList: React.FC<ICheckFileListProps> = ({ ID_TipoFileRichiesto, ValueJSON, OnChange }) => {
    const classes = useStyles();
    const { ShowDialog } = useContext(AlertDialogContext);
    
    const { ID_Anag, ID_FideAffidamento, ID_Polizza } = useParams<IRouteParam>();
    const [Data, setData] = useReducerOnChange<Dati.CDatiCheckListFile>(new Dati.CDatiCheckListFile());
    const Value: Dati.CDatiCheckListFileBase = ValueJSON ? JSON.parse(ValueJSON) : null;

    useEffect(() => {
        GetFilesData();
    }, [ID_Anag, ID_FideAffidamento, ID_Polizza, ID_TipoFileRichiesto]);
    
    const CountValue = (): Dati.CDatiCheckListFile => {
        const updatedData = { ...Data };

        if (Value) {
            for (let i: number = 0; i < Value.FileCaricati.length; i++) {
                for (let x: number = 0; x < Data.FileCaricati.length; x++) {
                    if (Data.FileCaricati[x].ID_File == Value.FileCaricati[i].ID_File) {
                        updatedData.FileCaricati[x].Selezionato = true;
                    }
                }
            }

            updatedData.Note = Value.Note;
            updatedData.Validato = Value.Validato;
        }
        return updatedData;
    };
    
    const GetFilesData = (): void => {
        let keys = ID_Anag + ',' + ((ID_FideAffidamento != undefined) ? ID_FideAffidamento + ',' : '') + ID_Polizza;
        const Param: CChiamaWebParametri = {
            URL: `/api/File/SelezionaFile?chiavi=${keys}&ID_TipoFileRichiesto=${ID_TipoFileRichiesto}`,
            Metodo: "GET",
            OnRis: OnGetData,
            OnErrore: OnError
        };
        new ChiamaWeb().Chiama(Param);
    };

    const OnGetData = (sender: ChiamaWeb, data: Dati.CRisAPIListaFileChecklist) => {
        setData({ Type: "OnChangeFromValue", Name: "FileCaricati", Value: data.Dati });
    };

    const OnError = (sender: ChiamaWeb, error: Error) => {
        ShowDialog("ERRORE", error.message, "error");
    };

    const CheckValidity = (): boolean => {
        let isValid = true;

        for (let i: number = 0; i < Data.FileCaricati.length; i++) {
            if (Data.FileCaricati[i].Necessario && (Data.FileCaricati[i].ID_TipoFileStato == Glob.Tipi_FS_Caricato || !Data.FileCaricati[i].Selezionato)) {
                isValid = false;
            }
        }
        return isValid;
    };

    const OnValidate = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (valid) {
            OnChange("Dato.FileJSON", JSON.stringify({ ...Value, Validato: e.currentTarget.checked }));
        } else {
            ShowDialog("WARNING", Trad.Trad("Non tutti i file necessari sono stati selezionati e validati"), "warning");
        }
    };

    const OnSelectedUpdate = (index: number, selected: boolean) => {
        const updatedData = { ...Data };

        updatedData.FileCaricati[index].Selezionato = selected;

        if (selected && updatedData.FileCaricati[index].Necessario) {
            for (let x: number = 0; x < updatedData.FileCaricati.length; x++) {
                if (updatedData.FileCaricati[x].Tipo == updatedData.FileCaricati[index].Tipo && x != index) {
                    updatedData.FileCaricati[x].Selezionato = false;
                    updatedData.FileCaricati[x].Necessario = false;
                }
            }
        } else if (!selected && updatedData.FileCaricati[index].Necessario) {
            for (let x: number = 0; x < updatedData.FileCaricati.length; x++) {
                if (updatedData.FileCaricati[x].Tipo == updatedData.FileCaricati[index].Tipo) {
                    updatedData.FileCaricati[x].Necessario = updatedData.FileCaricati[index].Necessario;
                }
            }
            OnChange("Dato.FileJSON", JSON.stringify({ ...Value, Validato: false }));
        }

        setData({ Type: "Set", Value: updatedData });
        OnUpdate();
    };

    const OnUpdate = () => {
        let appData: Dati.CDatiCheckListFileBase = new Dati.CDatiCheckListFileBase();

        appData.Note = Value.Note;
        appData.Validato = Value.Validato;
        appData.FileCaricati = [];
        for (let i: number = 0; i < Data.FileCaricati.length; i++) {
            if (Data.FileCaricati[i].Selezionato) appData.FileCaricati.push(new Dati.CDatiFileCheckListBase(Data.FileCaricati[i].ID_File));
        }
        OnChange("Dato.FileJSON", JSON.stringify(appData));
    };

    const updatedValue = CountValue();
    const valid = CheckValidity();

    return (
        <>
            <Toolbar
                variant="dense"
                className={clsx(classes.toolbar, {
                    warning: !updatedValue.Validato,
                    success: updatedValue.Validato
                })}
            >
                <FileCopyIcon className={classes.icon} />
                <Typography variant="subtitle1" color="inherit" className={classes.title}>
                    {Trad.Trad("Documenti")}
                </Typography>
                <Switch checked={updatedValue.Validato} name="Validato" onChange={OnValidate} />
                <a href={`/file/${ID_Polizza}/0`}>
                    <Tooltip title={Trad.Trad("Nuovo")}>
                        <IconButton size="small" aria-label="add document">
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </a>
            </Toolbar>
            <Table size="small" aria-label="check files table" className={classes.table}>
                <TableBody>
                    {updatedValue.FileCaricati.map((file, index) => (
                        <_CheckFileListRow
                            key={file.ID_File}
                            File={file}
                            Index={index}
                            OnSelectedUpdate={OnSelectedUpdate}
                            OnFileChange={(name, val) => setData({ Type: "OnChangeFromValue", Name: name, Value: val })}
                        />
                    ))}
                </TableBody>
            </Table>
            <TextField
                multiline
                fullWidth
                variant="outlined"
                id="Note"
                label={Trad.Trad("Note")}
                rows={3}
                rowsMax={5}
                value={updatedValue.Note}
                onChange={e => {
                    OnChange("Dato.FileJSON", JSON.stringify({ ...Value, Note: e.currentTarget.value }));
                }}
            />
        </>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/CheckFileList/CheckFileList.tsx